import React from "react";

import { FieldWrapperProps } from "@autoform/react";

import { CuratedBadge } from "@/components/field-curation/curated-badge";
import { useFieldCuration } from "@/components/field-curation/curated-fields-context";
import { Label } from "@/components/ui/label";

/**
 * Field types whose component draws its own label, or no label at all.
 *
 * `hidden` renders nothing — `HiddenField` is `() => null` — so a label over it is
 * a dangling word with an empty row under it. The nested `metadata.confidence` and
 * `ticketing.provider` have been doing that inside their object dialogs, and the
 * events edit form's `enrichment` field is the first one at the top level.
 * `venueTba` is a checkbox that draws its own "Venue TBA" label.
 */
const DISABLED_LABELS = ["boolean", "object", "array", "hidden", "venueTba"];

export const FieldWrapper: React.FC<FieldWrapperProps> = ({
  label,
  children,
  id,
  field,
  error,
}) => {
  const isDisabled = DISABLED_LABELS.includes(field.type);
  const isBoolean = field.type === "boolean";
  const { curated } = useFieldCuration(id);

  return (
    <div className="space-y-2">
      {!isDisabled && (
        <Label htmlFor={id}>
          {label}
          {field.required && <span className="text-destructive"> *</span>}
          <CuratedBadge fieldKey={id} />
        </Label>
      )}
      {/* `boolean` fields draw their own label inside `children`
          (`BooleanField`), so the disabled-label block above never fires for
          them — the marker goes here instead. Curated, it shares one
          inline-flex row with the switch so it reads as attached to it rather
          than a floating line below; uncurated, this renders bare `children` —
          byte-for-byte what a boolean field looked like before curated fields
          existed (no wrapper element, and `CuratedBadge` itself renders `null`). */}
      {isBoolean && curated ? (
        <div className="inline-flex items-center gap-1">
          {children}
          <CuratedBadge fieldKey={id} />
        </div>
      ) : (
        children
      )}
      {field.fieldConfig?.description && (
        <p className="text-sm text-muted-foreground">
          {field.fieldConfig.description}
        </p>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
};
